import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface TrendingTx {
  signature: string;
  slot: number;
  blockTime: number | null;
  tag: string;
  complexity: 'Simple' | 'Medium' | 'Complex';
}

const FALLBACK: TrendingTx[] = [
  {
    signature: '3ES1DvuWo1QaZaChpcgeqydBdBJgmVmJaAYAZ4XJ7h2xAx9Hw4kkvauV9bRbNJoSyfs3QToBHkC3GshXYhSro6up',
    slot: 425287267,
    blockTime: 1780992021,
    tag: 'System Program',
    complexity: 'Simple',
  },
  {
    signature: '38YByWC1ygVaxm6h7NHrWsGVvwLVBnfce86CwEtTURP3Ae7DSynnAqTKETSW7rVKEkmmynWdiMSEcZ8Q5rSknrxU',
    slot: 425287260,
    blockTime: 1780991996,
    tag: 'System Program',
    complexity: 'Simple',
  },
  {
    signature: '5cAWG2gPhbSkGkRicBDU9hja8jJVxDScpQmT55BrALv7NWRr4mSg4Xh8AA6WoVn5wjd2y7mjivwvBBqaBCjrkhja',
    slot: 425287250,
    blockTime: 1780991958,
    tag: 'Compute Budget',
    complexity: 'Complex',
  },
];

const complexityColor: Record<string, string> = {
  Simple: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  Medium: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  Complex: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
};

const tagColor = 'bg-slate-800 text-slate-400';

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function SuggestedTransactions() {
  const navigate = useNavigate();
  const [items, setItems] = useState<TrendingTx[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/api/trending`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: TrendingTx[]) => {
        setItems(data.slice(0, 9));
        setLoading(false);
      })
      .catch(() => {
        setItems(FALLBACK);
        setError(true);
        setLoading(false);
      });
  }, []);

  const handleClick = useCallback(
    (sig: string) => {
      navigate('/tx/' + encodeURIComponent(sig));
    },
    [navigate]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-300">
          🔥 Trending Examples
        </h2>
        <span className="text-xs text-slate-500">
          {error ? 'Showing fallback examples' : 'Fresh from mainnet'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
          : items.map((tx) => (
              <button
                key={tx.signature}
                onClick={() => handleClick(tx.signature)}
                className="text-left rounded-xl border border-slate-800 bg-slate-900/60 p-4 hover:border-violet-500/40 hover:bg-slate-800/60 transition-all group"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-sm font-medium text-slate-200 group-hover:text-violet-300 transition-colors">
                    {tx.tag}
                  </h3>
                  <span
                    className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${complexityColor[tx.complexity]}`}
                  >
                    {tx.complexity}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${tagColor}`}>
                    {tx.tag}
                  </span>
                </div>

                <div className="mt-3 flex items-center justify-between text-[10px] text-slate-600 font-mono">
                  <span>
                    {tx.signature.slice(0, 12)}…{tx.signature.slice(-6)}
                  </span>
                  <span className="text-slate-700">Slot {tx.slot.toLocaleString()}</span>
                </div>
              </button>
            ))}
      </div>

      <div className="rounded-lg border border-slate-800/60 bg-slate-900/40 px-4 py-3">
        <p className="text-xs text-slate-500">
          💡 <span className="text-slate-400 font-medium">Tip:</span>{' '}
          Paste any signature from{' '}
          <a
            href="https://solscan.io"
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-400 hover:text-violet-300 underline underline-offset-2"
          >
            Solscan
          </a>
          ,{' '}
          <a
            href="https://explorer.solana.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-400 hover:text-violet-300 underline underline-offset-2"
          >
            Solana Explorer
          </a>
          , or{' '}
          <a
            href="https://solana.fm"
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-400 hover:text-violet-300 underline underline-offset-2"
          >
            SolanaFM
          </a>
          {' '}to inspect it here.
        </p>
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 animate-pulse">
      <div className="flex items-start justify-between gap-2">
        <div className="h-4 w-24 rounded bg-slate-800" />
        <div className="h-4 w-14 rounded-full bg-slate-800" />
      </div>
      <div className="mt-3 h-3 w-16 rounded bg-slate-800" />
      <div className="mt-3 flex items-center justify-between">
        <div className="h-3 w-32 rounded bg-slate-800" />
        <div className="h-3 w-20 rounded bg-slate-800" />
      </div>
    </div>
  );
}
